import { NextRouter, useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * URL-persisted list filters (saved filter drawer, instant toolbar, debounced search).
 * Behavioral contract: `components/filters/__tests__/useSavedFilter.url.test.ts`.
 */

import DeepDiff from "deep-diff";
import { EnumLike, z } from "zod";

import { SortEnum } from "~/shared/schema/table-sort";
import { type UseFormReturnType, useDebouncedValue, useForm, zodResolver } from "~/ui";

import { parseSavedFilterCalendarDay, serializeSavedFilterCalendarDay } from "./saved-filter-calendar-day";

export { filterUuid, filterUuidArray } from "~/shared/schema/table-filters";
export { orderByWithArchivedFirstForQuery, SortEnum, sortToOrderBy } from "~/shared/schema/table-sort";

type DefaultType = Record<string, unknown>;

export const filterDate = () => z.date().nullable();
export const filterDateRange = () => filterDate().array().length(2);

export const filterRequiredDate = () => z.date();
export const filterRequiredDateRange = () => filterRequiredDate().array().length(2);

/**
 * Parsed query values are often strings, but some stacks pass `string[]` for repeated keys.
 * `z.coerce.boolean()` uses `Boolean(x)`, so `"false"` or `["false"]` would become `true`.
 * Normalize the first string element (trim + lowercase) before coercion.
 */
export const filterBoolean = () =>
  z.preprocess((val: unknown) => {
    const v = Array.isArray(val) ? val[0] : val;
    if (typeof v === "string") {
      const s = v.trim().toLowerCase();
      if (s === "true" || s === "1") {
        return true;
      }
      if (s === "false" || s === "0") {
        return false;
      }
    }
    return v;
  }, z.coerce.boolean());
export const filterBooleanArray = () => filterBoolean().array();

export const filterString = () => z.string();
export const filterStringArray = () => filterString().array();

export const filterNumber = () => z.union([z.literal(""), z.coerce.number()]);
export const filterNumberArray = () => filterNumber().array();

export const filterEnum = <T extends EnumLike>(values: T) => z.preprocess(v => String(v).toUpperCase(), z.nativeEnum(values));
export const filterEnumArray = <T extends EnumLike>(values: T) => filterEnum(values).array();

const urlInputDate = () =>
  z.coerce
    .string()
    .nullable()
    .refine(
      v => v === null || String(v).length === 0 || /\d{4}-\d{2}-\d{2}/.test(v),
      val => ({ message: `Invalid date: ${val}` }),
    )
    // Use noon UTC (T12:00:00Z) instead of local midnight to ensure the date string
    // represents the same calendar date regardless of user's browser timezone.
    // The server normalizes to start/end-of-day in org timezone, so noon UTC
    // provides a ±12 hour buffer that survives any timezone conversion.
    // This matches the pattern used in income-statement.tsx (atNoon function).
    .transform(v => (v === null || String(v).length === 0 ? null : new Date(`${v}T12:00:00Z`)));

const urlOutputDate = () =>
  z
    .date()
    .nullish()
    .transform(v => (v === null || v === undefined ? "" : (v.toISOString().split("T")[0] as string)));

/**
 * Org-calendar-day filter field (an "as of" lens). Unlike filterDate, the URL carries the plain
 * calendar day and hydration returns a browser-local picker Date; the consumer must anchor it to
 * the organization timezone before querying. The description marker scopes the calendar-day codec
 * and invalid-parameter scrubbing to exactly these fields, leaving filterDate consumers on the
 * noon-UTC transport above.
 */
export const CALENDAR_DAY_FILTER_DESCRIPTION = "saved-filter-calendar-day";
export const filterCalendarDay = () => z.date().nullable().describe(CALENDAR_DAY_FILTER_DESCRIPTION);
// biome-ignore-start lint/suspicious/noExplicitAny: carried over from the ESLint suppression
const isCalendarDayField = (schema: z.ZodSchema<any>): boolean => (schema as z.ZodTypeAny).description === CALENDAR_DAY_FILTER_DESCRIPTION;
// biome-ignore-end lint/suspicious/noExplicitAny: carried over from the ESLint suppression

const urlInputCalendarDay = () => z.unknown().transform(parseSavedFilterCalendarDay);

const urlOutputCalendarDay = () => z.date().nullish().transform(serializeSavedFilterCalendarDay);

/**
 * Sentinel value used in URL parameters to represent an explicitly empty array filter.
 * This allows us to distinguish between "user cleared filter" (empty array) and
 * "use default values" (no URL parameter present).
 * When this sentinel is present in the URL, loadFormUrl will restore an empty array
 * instead of falling back to defaults.
 *
 * The sentinel uses a namespaced format to avoid collision with legitimate filter values:
 * - UUIDs follow format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
 * - Enum values are uppercase words: UNBILLED, COMPLETED, etc.
 * - This sentinel uses a format that cannot match either pattern.
 */
const EMPTY_ARRAY_SENTINEL = "__SC_EMPTY_FILTER_ARRAY__";
const SAVED_FILTER_URL_CHANGE_EVENT = "saved-filter-url-change";

/**
 * Minimal URL search param surface used when merging query keys into table filter defaults.
 * Satisfied by `URLSearchParams` from the Pages Router URL snapshot and tests.
 */
export type UrlSearchParamsForFilters = Pick<URLSearchParams, "get" | "getAll" | "keys">;

/**
 * Preserves repeated query keys and explicit-empty-array sentinels while reading a Pages Router URL.
 * `router.query` flattens that information, so filters must derive their state from `router.asPath`.
 */
export function searchParamsFromPagesRouterPath(path: string): URLSearchParams {
  const queryStart = path.indexOf("?");
  if (queryStart < 0) {
    return new URLSearchParams();
  }
  const hashStart = path.indexOf("#", queryStart);
  return new URLSearchParams(path.slice(queryStart + 1, hashStart < 0 ? undefined : hashStart));
}

/** Build a Pages Router `query` object from URLSearchParams (repeated keys become string[]). */
export function searchParamsToRouterQuery(params: URLSearchParams): Record<string, string | string[]> {
  const query: Record<string, string | string[]> = {};
  for (const key of new Set(params.keys())) {
    const values = params.getAll(key);
    query[key] = values.length === 1 ? (values[0] ?? "") : values;
  }
  return query;
}

export function removeUrlSearchParameters(params: URLSearchParams, paramsToClear: readonly string[]): URLSearchParams {
  const nextParams = new URLSearchParams(params);
  for (const param of paramsToClear) {
    nextParams.delete(param);
  }
  return nextParams;
}

function pagesRouterPath(router: NextRouter): string {
  if (typeof window !== "undefined") {
    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
  }
  return router.isReady ? router.asPath : "";
}

/** Register saved-filter cross-path lock listeners on a Pages Router instance. */
export function attachSavedFilterRouteLifecycleListeners(router: Pick<NextRouter, "events">): () => void {
  const onRouteChangeComplete = (url: string) => {
    notifySavedFilterRouteChangeSettled(url);
  };
  const onRouteChangeError = (_err: Error, url: string) => {
    releaseStaleSavedFilterCrossPathLockAfterFailedNavigation(url);
  };

  router.events.on("routeChangeStart", notifySavedFilterRouteChangeStart);
  router.events.on("routeChangeComplete", onRouteChangeComplete);
  router.events.on("routeChangeError", onRouteChangeError);
  return () => {
    router.events.off("routeChangeStart", notifySavedFilterRouteChangeStart);
    router.events.off("routeChangeComplete", onRouteChangeComplete);
    router.events.off("routeChangeError", onRouteChangeError);
  };
}

/** Register once in `_app` so cross-path lock lifecycle is not missed on pages without saved-filter hooks. */
export function useSavedFilterRouteLifecycle(router: NextRouter): void {
  useEffect(() => attachSavedFilterRouteLifecycleListeners(router), [router]);
}

function usePagesRouterSearchParams(router: NextRouter): URLSearchParams {
  const [asPath, setAsPath] = useState(() => pagesRouterPath(router));

  useEffect(() => {
    const syncPath = () => {
      const nextPath = pagesRouterPath(router);
      setAsPath(currentPath => (currentPath === nextPath ? currentPath : nextPath));
    };

    syncPath();
    router.events.on("routeChangeComplete", syncPath);
    router.events.on("routeChangeError", syncPath);
    router.events.on("hashChangeComplete", syncPath);
    window.addEventListener("popstate", syncPath);
    window.addEventListener(SAVED_FILTER_URL_CHANGE_EVENT, syncPath);
    return () => {
      router.events.off("routeChangeComplete", syncPath);
      router.events.off("routeChangeError", syncPath);
      router.events.off("hashChangeComplete", syncPath);
      window.removeEventListener("popstate", syncPath);
      window.removeEventListener(SAVED_FILTER_URL_CHANGE_EVENT, syncPath);
    };
  }, [router]);

  return useMemo(() => searchParamsFromPagesRouterPath(asPath), [asPath]);
}

// biome-ignore-start lint/suspicious/noExplicitAny: carried over from the ESLint suppression
const findBaseType = (schema: z.ZodSchema<any>): z.ZodTypeAny => {
  // biome-ignore-end lint/suspicious/noExplicitAny: carried over from the ESLint suppression
  if (schema instanceof z.ZodEffects) {
    return findBaseType(schema.sourceType());
  }

  if (schema instanceof z.ZodOptional || schema instanceof z.ZodNullable) {
    return findBaseType(schema.unwrap());
  }

  if (schema instanceof z.ZodPipeline) {
    return findBaseType(schema._def.in);
  }

  if (schema instanceof z.ZodArray) {
    return findBaseType(schema.element);
  }

  return schema;
};

export function useWorkNextTick(work: () => void | Promise<void>) {
  const [workTick, setWorkTick] = useState(0);
  const workRef = useRef(work);
  workRef.current = work;

  useEffect(() => {
    if (workTick > 0) {
      // Auto-apply paths must not surface router.replace failures as unhandled rejections.
      void Promise.resolve(workRef.current()).catch(() => undefined);
    }
  }, [workTick]);

  // Functional updates cannot collide when repeated edits happen within the same wall-clock second.
  return useCallback(() => setWorkTick(current => current + 1), []);
}

// biome-ignore-start lint/suspicious/noExplicitAny: carried over from the ESLint suppression
function loadFormUrl<T extends z.ZodObject<any>>(schema: T, defaults: DefaultType = {}, input: UrlSearchParamsForFilters): z.output<T> {
  // biome-ignore-end lint/suspicious/noExplicitAny: carried over from the ESLint suppression
  const shape = schema.shape;
  const schemaKeys = Object.keys(shape);
  const inputKeys = Array.from(input.keys());
  const defaultsKeys = Object.keys(defaults);
  const toDate = urlInputDate();
  const toCalendarDay = urlInputCalendarDay();

  const isFieldCalendarDay = Object.keys(shape).reduce<Record<string, boolean>>((acc, field) => {
    acc[field] = isCalendarDayField(shape[field]);
    return acc;
  }, {});
  const isFieldDate = Object.keys(shape).reduce<Record<string, boolean>>((acc, field) => {
    acc[field] = !isFieldCalendarDay[field] && findBaseType(shape[field]) instanceof z.ZodDate;
    return acc;
  }, {});

  const getValue = (field: string, isArray: boolean) => {
    const raw = isArray ? input.getAll(field) : [input.get(field)];

    // Handle sentinel value for empty arrays: if we see the sentinel, return empty array
    // This distinguishes "user cleared filter" from "use defaults"
    if (isArray && raw.length === 1 && raw[0] === EMPTY_ARRAY_SENTINEL) {
      return [];
    }

    const dateCodec = isFieldCalendarDay[field] ? toCalendarDay : isFieldDate[field] ? toDate : null;
    const values = dateCodec
      ? raw.map(v => {
          const result = dateCodec.safeParse(v);
          return result.success ? result.data : null;
        })
      : raw;
    return isArray ? values : values[0];
  };

  const results: Record<string, unknown> = {};

  for (const field of schemaKeys) {
    // If field exists in URL (including sentinel), process it; otherwise use default
    if (inputKeys.includes(field)) {
      const isArray = shape[field] instanceof z.ZodArray;
      const value = getValue(field, isArray);
      // If we got an empty array from sentinel, use it directly (don't parse)
      if (isArray && Array.isArray(value) && value.length === 0) {
        results[field] = [];
      } else {
        const result = shape[field].safeParse(value);
        if (result.success) {
          results[field] = result.data;
        } else if (defaultsKeys.includes(field)) {
          // Fall back to default if parsing fails
          results[field] = defaults[field];
        }
      }
    } else if (defaultsKeys.includes(field)) {
      // No URL parameter present, use default
      results[field] = defaults[field];
    }
  }

  return results;
}

/**
 * Parses URL query params into schema-validated values. For each key in the schema: if that key
 * appears in `input`, the URL value wins (after parse); otherwise the value comes from `defaults`.
 */
// biome-ignore-start lint/suspicious/noExplicitAny: carried over from the ESLint suppression
export function loadFilterValuesFromUrlParams<T extends z.ZodObject<any>>(
  // biome-ignore-end lint/suspicious/noExplicitAny: carried over from the ESLint suppression
  schema: T,
  defaults: DefaultType,
  input: UrlSearchParamsForFilters,
): z.output<T> {
  return loadFormUrl(schema, defaults, input);
}

// Invalid calendar-day query values must be removed before a consumer enables its read. Returning
// null is not enough: it would silently turn a malformed projection into a live-now request. Scoped
// to filterCalendarDay fields; filterDate fields keep their long-standing lax noon-UTC handling.
// biome-ignore-start lint/suspicious/noExplicitAny: carried over from the ESLint suppression
export function invalidSavedFilterDateParameters<T extends z.ZodObject<any>>(schema: T, input: UrlSearchParamsForFilters): string[] {
  // biome-ignore-end lint/suspicious/noExplicitAny: carried over from the ESLint suppression
  const shape = schema.shape;
  const inputKeys = Array.from(input.keys());
  return Object.keys(shape).flatMap(field => {
    if (!isCalendarDayField(shape[field]) || !inputKeys.includes(field)) {
      return [];
    }
    return parseSavedFilterCalendarDay(input.get(field)) ? [] : [field];
  });
}

export class SavedFilterRouteChangeError extends Error {
  constructor() {
    super("Saved filter URL write dropped after route change.");
    this.name = "SavedFilterRouteChangeError";
  }
}

// Bumped when a Pages Router navigation to a different pathname starts. Pathname alone is not enough:
// during in-flight navigation `window.location.pathname` still shows the source route.
let savedFilterRouteGeneration = 0;
// Set for the duration of a cross-path navigation so source-page writes enqueued after start
// (debounced search / next-tick filters) cannot adopt the new generation and replace the source URL.
let savedFilterCrossPathDestination: string | null = null;
// Count overlapping starts to the same destination so a cancelled nav's error cannot clear a
// newer in-flight navigation to that same pathname.
let savedFilterCrossPathInFlight = 0;

function pathnameFromRouterUrl(url: string): string {
  const pathWithQuery = url.startsWith("http") ? new URL(url).pathname + new URL(url).search + new URL(url).hash : url;
  const queryStart = pathWithQuery.indexOf("?");
  const hashStart = pathWithQuery.indexOf("#");
  const end =
    queryStart >= 0 && hashStart >= 0 ? Math.min(queryStart, hashStart) : queryStart >= 0 ? queryStart : hashStart >= 0 ? hashStart : -1;
  return end < 0 ? pathWithQuery : pathWithQuery.slice(0, end);
}

/** Strip Next.js i18n locale prefix so `/en/org-demo/...` matches `/org-demo/...` in router events. */
function normalizeSavedFilterPathname(pathname: string): string {
  const withLeadingSlash = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const parts = withLeadingSlash.split("/").filter(Boolean);
  if (parts[0] === "en") {
    return parts.length === 1 ? "/" : `/${parts.slice(1).join("/")}`;
  }
  return withLeadingSlash;
}

/** Dynamic route templates in router events ([id]) must match resolved browser pathnames. */
export function savedFilterPathnamesMatch(left: string, right: string): boolean {
  const normalizedLeft = normalizeSavedFilterPathname(left);
  const normalizedRight = normalizeSavedFilterPathname(right);
  if (normalizedLeft === normalizedRight) {
    return true;
  }
  const leftParts = normalizedLeft.split("/").filter(Boolean);
  const rightParts = normalizedRight.split("/").filter(Boolean);
  if (leftParts.length !== rightParts.length) {
    return false;
  }
  return leftParts.every((leftPart, index) => {
    const rightPart = rightParts[index];
    if (leftPart === undefined || rightPart === undefined) {
      return false;
    }
    if (leftPart.startsWith("[") && leftPart.endsWith("]")) {
      return true;
    }
    if (rightPart.startsWith("[") && rightPart.endsWith("]")) {
      return true;
    }
    return leftPart === rightPart;
  });
}

/** Test seam: simulate routeChangeStart to a different pathname. */
export function notifySavedFilterRouteChangeStart(nextUrl: string): void {
  const nextPathname = pathnameFromRouterUrl(nextUrl);
  const currentPathname = typeof window !== "undefined" ? window.location.pathname : "";
  if (nextPathname.length > 0 && !savedFilterPathnamesMatch(nextPathname, currentPathname)) {
    savedFilterRouteGeneration += 1;
    if (savedFilterCrossPathDestination !== null && savedFilterPathnamesMatch(savedFilterCrossPathDestination, nextPathname)) {
      savedFilterCrossPathInFlight += 1;
    } else {
      savedFilterCrossPathDestination = nextPathname;
      savedFilterCrossPathInFlight = 1;
    }
  }
}

/**
 * Clear cross-path ownership when that navigation settles.
 * Same-path saved-filter `replace` also emits complete/error — only clear when the settled
 * URL pathname matches the tracked destination, otherwise an overlapping same-path write can
 * reopen the stale-write window mid cross-path navigation.
 * Overlapping starts to the same destination are refcounted so a cancelled nav's settle cannot
 * clear a newer in-flight navigation.
 * Omit `settledUrl` only in tests to force-clear module state.
 */
export function notifySavedFilterRouteChangeSettled(settledUrl?: string): void {
  if (savedFilterCrossPathDestination === null) {
    return;
  }
  if (settledUrl === undefined) {
    savedFilterCrossPathDestination = null;
    savedFilterCrossPathInFlight = 0;
    return;
  }
  if (!savedFilterPathnamesMatch(pathnameFromRouterUrl(settledUrl), savedFilterCrossPathDestination)) {
    return;
  }
  savedFilterCrossPathInFlight = Math.max(0, savedFilterCrossPathInFlight - 1);
  if (savedFilterCrossPathInFlight === 0) {
    savedFilterCrossPathDestination = null;
  }
}

/** Compare query strings regardless of parameter ordering. */
function savedFilterSearchStringsEquivalent(left: string, right: string): boolean {
  const normalize = (search: string) => {
    const raw = search.startsWith("?") ? search.slice(1) : search;
    const params = new URLSearchParams(raw);
    const sortedEntries = [...params.entries()].sort(([aKey, aVal], [bKey, bVal]) => aKey.localeCompare(bKey) || aVal.localeCompare(bVal));
    return sortedEntries.map(([key, value]) => `${key}=${value}`).join("&");
  };
  return normalize(left) === normalize(right);
}

function savedFilterBrowserUrlMatchesTarget(targetUrl: string): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const queryStart = targetUrl.indexOf("?");
  const targetPath = queryStart >= 0 ? targetUrl.slice(0, queryStart) : targetUrl;
  const targetSearch = queryStart >= 0 ? targetUrl.slice(queryStart) : "";
  return (
    savedFilterPathnamesMatch(window.location.pathname, targetPath) &&
    savedFilterSearchStringsEquivalent(window.location.search, targetSearch)
  );
}

/**
 * When a cross-path navigation fails or is cancelled, Next may emit routeChangeError with the
 * source URL while a stale destination lock is still armed — release it so same-page filter writes work again.
 */
export function releaseStaleSavedFilterCrossPathLockAfterFailedNavigation(failedNavigationUrl: string): void {
  notifySavedFilterRouteChangeSettled(failedNavigationUrl);
  if (savedFilterCrossPathDestination === null || typeof window === "undefined") {
    return;
  }
  const settledPathname = pathnameFromRouterUrl(failedNavigationUrl);
  const currentPathname = window.location.pathname;
  if (
    savedFilterPathnamesMatch(currentPathname, settledPathname) &&
    !savedFilterPathnamesMatch(currentPathname, savedFilterCrossPathDestination)
  ) {
    savedFilterCrossPathDestination = null;
    savedFilterCrossPathInFlight = 0;
  }
}

function assertSavedFilterWriteStillOnOrigin(originatingPathname: string, originatingGeneration: number): void {
  if (
    savedFilterCrossPathDestination !== null &&
    originatingPathname.length > 0 &&
    !savedFilterPathnamesMatch(originatingPathname, savedFilterCrossPathDestination)
  ) {
    throw new SavedFilterRouteChangeError();
  }
  if (originatingGeneration !== savedFilterRouteGeneration) {
    throw new SavedFilterRouteChangeError();
  }
  if (
    originatingPathname.length > 0 &&
    typeof window !== "undefined" &&
    !savedFilterPathnamesMatch(window.location.pathname, originatingPathname)
  ) {
    throw new SavedFilterRouteChangeError();
  }
}

function applySavedFilterUrlToBrowserHistory(url: string): void {
  if (typeof window === "undefined") {
    return;
  }
  window.history.replaceState(window.history.state, "", url);
}

const PAGES_ROUTER_READY_WAIT_MS = 5000;

/** Bounded wait so queued filter URL writes cannot leave savedFilterUrlWritesInFlight stuck. */
function waitForPagesRouterReady(router: NextRouter): Promise<void> {
  if (router.isReady) {
    return Promise.resolve();
  }
  return new Promise<void>(resolve => {
    let settled = false;
    const finish = () => {
      if (settled) {
        return;
      }
      settled = true;
      router.events.off("routeChangeComplete", onRouteChangeComplete);
      clearTimeout(timeoutId);
      resolve();
    };
    const onRouteChangeComplete = () => {
      finish();
    };
    router.events.on("routeChangeComplete", onRouteChangeComplete);
    const timeoutId = setTimeout(finish, PAGES_ROUTER_READY_WAIT_MS);
  });
}

// biome-ignore-start lint/suspicious/noExplicitAny: carried over from the ESLint suppression
async function saveToUrl<T extends z.ZodObject<any>, S>(
  // biome-ignore-end lint/suspicious/noExplicitAny: carried over from the ESLint suppression
  schema: T,
  form: UseFormReturnType<S>,
  defaults: DefaultType,
  router: NextRouter,
  paramsToClear: string[] = [],
  originatingPathname: string = typeof window !== "undefined" ? window.location.pathname : "",
  originatingGeneration: number = savedFilterRouteGeneration,
  valuesSnapshot?: S,
): Promise<string> {
  assertSavedFilterWriteStillOnOrigin(originatingPathname, originatingGeneration);
  let params = new URLSearchParams(location.search);
  const values = valuesSnapshot ?? form.values;
  const shape = schema.shape;
  const isFieldCalendarDay = Object.keys(shape).reduce<Record<string, boolean>>((acc, field) => {
    acc[field] = isCalendarDayField(shape[field]);
    return acc;
  }, {});
  const isFieldDate = Object.keys(shape).reduce<Record<string, boolean>>((acc, field) => {
    acc[field] = !isFieldCalendarDay[field] && findBaseType(shape[field]) instanceof z.ZodDate;
    return acc;
  }, {});
  const fromDate = urlOutputDate();
  const fromCalendarDay = urlOutputCalendarDay();

  const getValue = (field: string, isArray: boolean) => {
    // biome-ignore-start lint/suspicious/noTsIgnore: carried over from the ESLint suppression
    // @ts-ignore
    const raw = isArray ? values[field] : [values[field]];
    // biome-ignore-end lint/suspicious/noTsIgnore: carried over from the ESLint suppression

    const dateCodec = isFieldCalendarDay[field] ? fromCalendarDay : isFieldDate[field] ? fromDate : null;
    const serialized = dateCodec
      ? // biome-ignore-start lint/suspicious/noTsIgnore: carried over from the ESLint suppression
        // @ts-ignore
        raw.map(v => {
          // biome-ignore-end lint/suspicious/noTsIgnore: carried over from the ESLint suppression
          const result = dateCodec.safeParse(v);
          return result.success ? result.data : null;
        })
      : raw;
    return isArray ? serialized : serialized[0];
  };

  for (const field in values) {
    params.delete(field);

    const diffs = DeepDiff(defaults[field], values[field]);
    if (diffs == undefined || diffs[0] == undefined) {
      continue;
    }

    const isArray = shape[field] instanceof z.ZodArray;
    const value = getValue(field, isArray);
    if (!isArray) {
      params.set(field, value == null ? "" : String(value));
    } else {
      // For empty arrays, write the sentinel value to distinguish from "use defaults"
      // This ensures that clearing a filter persists as "show all" rather than reverting to defaults
      if (Array.isArray(value) && value.length === 0) {
        params.append(field, EMPTY_ARRAY_SENTINEL);
      } else {
        for (const v of value) {
          params.append(field, v);
        }
      }
    }
  }

  params = removeUrlSearchParameters(params, paramsToClear);

  assertSavedFilterWriteStillOnOrigin(originatingPathname, originatingGeneration);

  const pathName = originatingPathname.length > 0 ? originatingPathname : location.pathname;
  const url = pathName + (params.size === 0 ? "" : "?" + params.toString());
  if (!router.isReady) {
    await waitForPagesRouterReady(router);
  }
  let replaced = await router.replace(url, url, { shallow: true });
  if (!replaced && savedFilterBrowserUrlMatchesTarget(url)) {
    replaced = true;
  }
  if (!replaced) {
    applySavedFilterUrlToBrowserHistory(url);
    if (savedFilterBrowserUrlMatchesTarget(url)) {
      replaced = true;
    }
  }
  if (!replaced) {
    throw new Error("Saved filter URL change was cancelled.");
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(SAVED_FILTER_URL_CHANGE_EVENT));
  }
  return url;
}

// Filter controls on one page can use independent hooks (discrete filters + debounced search).
// Serialize their router writes so every writer reads the URL left by the preceding writer.
// Bind each write to the pathname + route-generation at enqueue time so in-flight navigations
// (pathname still source) and committed route changes cannot let a stale page rewrite the destination.
let savedFilterUrlWriteQueue: Promise<void> = Promise.resolve();
/** Queued or in-flight router.replace calls from any saved-filter hook on the page. */
let savedFilterUrlWritesInFlight = 0;

/** True while any saved-filter URL write is queued or executing (cross-hook hydration gate). */
export function savedFilterUrlWritesPending(): boolean {
  return savedFilterUrlWritesInFlight > 0;
}

export function queueSavedFilterUrlWrite<T>(write: () => Promise<T>): Promise<T> {
  savedFilterUrlWritesInFlight += 1;
  const originatingPathname = typeof window !== "undefined" ? window.location.pathname : "";
  const originatingGeneration = savedFilterRouteGeneration;
  const guardedWrite = async () => {
    assertSavedFilterWriteStillOnOrigin(originatingPathname, originatingGeneration);
    return write();
  };
  const queuedWrite = savedFilterUrlWriteQueue.then(guardedWrite, guardedWrite);
  savedFilterUrlWriteQueue = queuedWrite.then(
    () => undefined,
    () => undefined,
  );
  return queuedWrite.finally(() => {
    savedFilterUrlWritesInFlight = Math.max(0, savedFilterUrlWritesInFlight - 1);
    if (savedFilterUrlWritesInFlight === 0 && typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
      // Let every hook re-hydrate after a multi-writer page finishes its queue (URL may match asPath).
      window.dispatchEvent(new Event(SAVED_FILTER_URL_CHANGE_EVENT));
    }
  });
}

/** Wait until saved-filter URL writes already scheduled by the current interaction settle. */
export async function waitForSavedFilterUrlWrites(): Promise<void> {
  await savedFilterUrlWriteQueue;
}

export function commitSavedFilterValuesBeforeUrlWrite<TValues, TResult>(
  values: TValues,
  commit: (values: TValues) => void,
  write: () => Promise<TResult>,
): Promise<TResult> {
  commit(values);
  return queueSavedFilterUrlWrite(write);
}

interface SavedFilterWriteFailureContext<TValues> {
  writeVersion: number;
  currentWriteVersion: number;
  originatingPathname: string;
  currentPathname: string;
  loadCurrentValues: () => TValues;
  reconcile: (values: TValues) => void;
}

/** Gate URL → form hydration so in-flight instant-filter edits are not overwritten before applyFilter runs. */
export function shouldApplySavedFilterUrlHydration(pendingUrlWrite: boolean, formDirty: boolean, skipHydrationWhenDirty = false): boolean {
  if (pendingUrlWrite) {
    return false;
  }
  if (skipHydrationWhenDirty && formDirty) {
    return false;
  }
  return true;
}

export function reconcileSavedFilterValuesAfterWriteFailure<TValues>({
  writeVersion,
  currentWriteVersion,
  originatingPathname,
  currentPathname,
  loadCurrentValues,
  reconcile,
}: SavedFilterWriteFailureContext<TValues>): "stale" | "route-changed" | "reconciled" {
  if (writeVersion !== currentWriteVersion) {
    return "stale";
  }
  if (!savedFilterPathnamesMatch(currentPathname, originatingPathname)) {
    return "route-changed";
  }

  reconcile(loadCurrentValues());
  return "reconciled";
}

type SavedFilterInternalOptions = {
  /** Instant toolbars: skip URL hydration while dirty. Drawer/manual filters keep hydrating on back/forward. */
  skipHydrationWhenDirty?: boolean;
  /** When true, URL → form hydration is skipped (e.g. filter drawer open with in-progress edits). */
  shouldPauseHydration?: () => boolean;
};

// biome-ignore-start lint/suspicious/noExplicitAny: carried over from the ESLint suppression
function useSavedInternal<T extends z.ZodObject<any>>(
  // biome-ignore-end lint/suspicious/noExplicitAny: carried over from the ESLint suppression
  schema: T,
  defaults: DefaultType = {},
  options: SavedFilterInternalOptions = {},
): [z.output<T>, UseFormReturnType<z.output<T>>, (paramsToClear: string[], valuesSnapshot?: z.output<T>) => Promise<void>, boolean] {
  const skipHydrationWhenDirty = options.skipHydrationWhenDirty ?? false;
  const shouldPauseHydration = options.shouldPauseHydration;
  const router = useRouter();
  const searchParams = usePagesRouterSearchParams(router);
  const invalidDateParams = invalidSavedFilterDateParameters(schema, searchParams);
  const invalidDateParamsKey = invalidDateParams.join("\u0000");
  const dateUrlReady = invalidDateParams.length === 0;
  const searchValues = loadFormUrl(schema, defaults, searchParams);
  const [savedValues, setSavedValues] = useState<z.output<T>>(() => searchValues);
  // Instant toolbars only: match URL on first paint so controls are not dirty before hydration runs.
  const form = useForm<z.output<T>>({
    validate: zodResolver(schema),
    initialValues: skipHydrationWhenDirty ? searchValues : defaults,
  });
  const writeVersionRef = useRef(0);
  // While a URL write is in flight, ignore searchParams hydration. Another filter hook on the same
  // page can router.replace with a snapshot that omits this hook's optimistic field values; applying
  // that URL would revert form/saved state before the pending write completes.
  const pendingWriteRef = useRef(false);

  useEffect(() => {
    if (invalidDateParams.length === 0 || typeof window === "undefined") {
      return;
    }
    const nextParams = removeUrlSearchParameters(new URLSearchParams(window.location.search), invalidDateParams);
    const nextUrl = `${window.location.pathname}${nextParams.size === 0 ? "" : `?${nextParams.toString()}`}${window.location.hash}`;
    void queueSavedFilterUrlWrite(async () => {
      const replaced = await router.replace(nextUrl);
      if (replaced) {
        window.dispatchEvent(new Event(SAVED_FILTER_URL_CHANGE_EVENT));
      }
      return replaced;
    });
    // The joined key is the stable dependency; URLSearchParams and schema identities are not.
  }, [invalidDateParamsKey, router]);

  useEffect(() => {
    if (shouldPauseHydration?.()) {
      return;
    }
    if (savedFilterUrlWritesPending()) {
      return;
    }
    if (!shouldApplySavedFilterUrlHydration(pendingWriteRef.current, form.isDirty(), skipHydrationWhenDirty)) {
      return;
    }
    form.setValues(searchValues);
    form.resetDirty(searchValues);
    setSavedValues(searchValues);
  }, [searchParams]);

  const applyFilter = useCallback(
    (paramsToClear: string[], valuesSnapshot?: z.output<T>): Promise<void> => {
      const writeVersion = writeVersionRef.current + 1;
      writeVersionRef.current = writeVersion;
      const originatingPathname = typeof window !== "undefined" ? window.location.pathname : "";
      const originatingGeneration = savedFilterRouteGeneration;
      const nextValues = schema.parse(valuesSnapshot ?? form.values);
      pendingWriteRef.current = true;
      return commitSavedFilterValuesBeforeUrlWrite(nextValues, setSavedValues, () =>
        saveToUrl(schema, form, defaults, router, paramsToClear, originatingPathname, originatingGeneration, nextValues),
      )
        .then(url => {
          if (writeVersion === writeVersionRef.current) {
            const committedValues = loadFormUrl(schema, defaults, searchParamsFromPagesRouterPath(url));
            form.setValues(committedValues);
            form.resetDirty(committedValues);
            setSavedValues(committedValues);
          }
        })
        .catch(error => {
          reconcileSavedFilterValuesAfterWriteFailure({
            writeVersion,
            currentWriteVersion: writeVersionRef.current,
            originatingPathname,
            currentPathname: typeof window !== "undefined" ? window.location.pathname : "",
            loadCurrentValues: () => loadFormUrl(schema, defaults, searchParamsFromPagesRouterPath(pagesRouterPath(router))),
            reconcile: currentValues => {
              form.setValues(currentValues);
              form.resetDirty(currentValues);
              setSavedValues(currentValues);
            },
          });
          throw error;
        })
        .finally(() => {
          if (writeVersion === writeVersionRef.current) {
            pendingWriteRef.current = false;
          }
        });
    },
    [defaults, form, router, schema],
  );

  return [savedValues, form, applyFilter, dateUrlReady];
}

export type UseSavedFilterOptions = SavedFilterInternalOptions;

// biome-ignore-start lint/suspicious/noExplicitAny: carried over from the ESLint suppression
export function useSavedFilter<T extends z.ZodObject<any>>(
  // biome-ignore-end lint/suspicious/noExplicitAny: carried over from the ESLint suppression
  schema: T,
  defaults: DefaultType = {},
  options: UseSavedFilterOptions = {},
): [z.output<T>, UseFormReturnType<z.output<T>>, (valuesSnapshot?: z.output<T>) => Promise<void>, boolean] {
  const [values, form, applyFilterInner, dateUrlReady] = useSavedInternal(schema, defaults, options);

  const applyFilter = useCallback((valuesSnapshot?: z.output<T>) => applyFilterInner(["page"], valuesSnapshot), [applyFilterInner]);

  return [values, form, applyFilter, dateUrlReady];
}

// biome-ignore-start lint/suspicious/noExplicitAny: carried over from the ESLint suppression
export type InstantSavedFilterBar<T extends z.ZodObject<any>> = {
  // biome-ignore-end lint/suspicious/noExplicitAny: carried over from the ESLint suppression
  /** URL-committed values — use for queries, exports, and derived table state. */
  committed: z.output<T>;
  /** Mantine form — bind interactive control `value` props to `form.values`. */
  form: UseFormReturnType<z.output<T>>;
  /** False while malformed date params are being removed; consumers must keep reads disabled. */
  urlReady: boolean;
};

/**
 * URL-persists filter form values immediately after each edit (next tick).
 * Use one combined schema per toolbar: multiple instant hooks on the same page each
 * call router.replace and can race; pendingWriteRef in useSavedInternal prevents
 * hydration from reverting optimistic state while a write is in flight.
 *
 * Bind controls to `form.values`; read queries from `committed`.
 */
// biome-ignore-start lint/suspicious/noExplicitAny: carried over from the ESLint suppression
export function useInstantSavedFilterBar<T extends z.ZodObject<any>>(schema: T, defaults: DefaultType = {}): InstantSavedFilterBar<T> {
  // biome-ignore-end lint/suspicious/noExplicitAny: carried over from the ESLint suppression
  const [committed, form, applyFilter, urlReady] = useSavedInternal(schema, defaults, { skipHydrationWhenDirty: true });
  const currentValues = useRef(JSON.stringify(form.values));
  const work = useWorkNextTick(() => applyFilter(["page"]));

  useEffect(() => {
    const newValues = JSON.stringify(form.values) ?? "";
    if (currentValues.current === newValues) {
      return;
    }
    currentValues.current = newValues;
    work();
  }, [form.values]);

  return { committed, form, urlReady };
}

const QuerySchema = z.object({
  query: filterString(),
});

type DebouncedSavedQueryCommitAction = "wait-for-hydration" | "finish-hydration" | "skip" | "commit";

export function getDebouncedSavedQueryCommitAction(
  query: string,
  committedQuery: string,
  lastAppliedQuery: string,
  hydrationTarget: string | null,
): DebouncedSavedQueryCommitAction {
  if (hydrationTarget !== null) {
    return query === hydrationTarget && committedQuery === hydrationTarget ? "finish-hydration" : "wait-for-hydration";
  }
  return committedQuery === query && lastAppliedQuery !== committedQuery ? "commit" : "skip";
}

export function useDebouncedSavedQuery(debounceMs = 300): [string, string, (value: string) => void, () => void] {
  const [values, form, applyFilter] = useSavedInternal(QuerySchema, { query: "" });
  const urlQuery = values.query ?? "";
  const [query, setQuery] = useState(urlQuery);
  const [committedQuery] = useDebouncedValue(query, debounceMs);
  const lastAppliedQuery = useRef(urlQuery);
  const urlHydrationTarget = useRef<string | null>(null);
  const work = useWorkNextTick(() => applyFilter(["page"]));

  const setQueryFromInput = useCallback((value: string) => {
    urlHydrationTarget.current = null;
    setQuery(value);
  }, []);

  /** Drop the active search bar from local state and the URL immediately (e.g. after promoting a term to OR pins). */
  const clearCommittedQuery = useCallback(() => {
    urlHydrationTarget.current = null;
    lastAppliedQuery.current = "";
    setQuery("");
    form.setFieldValue("query", "");
    void applyFilter(["page"], { query: "" });
  }, [applyFilter, form]);

  useEffect(() => {
    if (urlQuery === lastAppliedQuery.current) {
      return;
    }
    urlHydrationTarget.current = urlQuery;
    setQuery(urlQuery);
    lastAppliedQuery.current = urlQuery;
  }, [urlQuery]);

  useEffect(() => {
    const action = getDebouncedSavedQueryCommitAction(query, committedQuery, lastAppliedQuery.current, urlHydrationTarget.current);
    if (action === "wait-for-hydration" || action === "skip") {
      return;
    }
    if (action === "finish-hydration") {
      // URL hydration owns query state until the debounced value reaches the same target.
      urlHydrationTarget.current = null;
      return;
    }
    lastAppliedQuery.current = committedQuery;
    form.setFieldValue("query", committedQuery);
    work();
  }, [committedQuery, form, query, work]);

  return [query, committedQuery, setQueryFromInput, clearCommittedQuery];
}

const PageSchema = z.object({
  page: z.coerce.number().positive(),
});
export function useSavedPageNumber(): [number, (value: number) => void] {
  const [values, form, applyFilter] = useSavedInternal(PageSchema, { page: 1 });
  const work = useWorkNextTick(() => applyFilter([]));

  const setPage = useCallback(
    (value: number) => {
      form.setFieldValue("page", value);
      work();
    },
    [form, work],
  );

  return [values.page, setPage];
}

const SortSchema = z.object({
  sortField: filterString(),
  sortDir: z.preprocess(v => String(v).toLowerCase(), SortEnum),
});
type SortSchemaType = z.infer<typeof SortSchema>;

const PageAndSortSchema = z.object({
  page: z.coerce.number().positive(),
  sortField: filterString(),
  sortDir: z.preprocess(v => String(v).toLowerCase(), SortEnum),
});
export type SavedPageAndSortValues = z.infer<typeof PageAndSortSchema>;

/** Compare pending form sort against the next selection — not committed URL sort (see useSavedPageAndSort). */
export function shouldSkipInstantPageAndSortWrite(
  nextSortField: string,
  nextSortDir: SortEnum,
  formSortField: string,
  formSortDir: SortEnum,
): boolean {
  if (nextSortField === "") {
    return true;
  }
  return nextSortField === formSortField && nextSortDir === formSortDir;
}

/** Single URL writer for pagination + sort — reduces multi-hook races on list toolbars. */
export function useSavedPageAndSort(
  defaultField = "createdAt",
  defaultDir: SortEnum = "desc",
): [SavedPageAndSortValues, (page: number) => void, (name: string, direction: SortEnum) => void] {
  const [values, form, applyFilter] = useSavedInternal(
    PageAndSortSchema,
    {
      page: 1,
      sortField: defaultField,
      sortDir: defaultDir,
    },
    { skipHydrationWhenDirty: true },
  );
  const work = useWorkNextTick(() => applyFilter([]));

  const setPage = useCallback(
    (value: number) => {
      form.setFieldValue("page", value);
      work();
    },
    [form, work],
  );

  const applySort = useCallback(
    (sortField: string, sortDir: SortEnum) => {
      if (shouldSkipInstantPageAndSortWrite(sortField, sortDir, form.values.sortField, form.values.sortDir)) {
        return;
      }
      form.setValues({
        ...form.values,
        sortField,
        sortDir,
      });
      work();
    },
    [form, work],
  );

  return [values, setPage, applySort];
}

export function useSavedSort(
  defaultField = "createdAt",
  defaultDir: SortEnum = "desc",
): [SortSchemaType, (name: string, direction: SortEnum) => void] {
  const [values, form, applyFilter] = useSavedInternal(SortSchema, { sortField: defaultField, sortDir: defaultDir });
  const work = useWorkNextTick(() => applyFilter([]));

  const applySort = useCallback(
    (sortField: string, sortDir: SortEnum) => {
      if (shouldSkipInstantPageAndSortWrite(sortField, sortDir, form.values.sortField, form.values.sortDir)) {
        return;
      }
      form.setValues({
        sortField,
        sortDir,
      });
      work();
    },
    [form, work],
  );

  return [values, applySort];
}
